import { useState, useEffect, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { productCategories } from '@/mocks/productsData';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { motion } from 'motion/react';

const origins = ['All Origins', 'China', 'Germany', 'Japan', 'Turkey', 'India'];

interface ProductFromDB {
  id: string;
  name: string;
  slug: string;
  category: string;
  subcategory: string | null;
  manufacturer: string;
  origin: string;
  price_min: number | null;
  price_max: number | null;
  description: string | null;
  images: string[] | null;
  featured: boolean;
}

interface ProductUI {
  id: string;
  slug: string;
  name: string;
  category: string;
  subcategory: string;
  manufacturer: string;
  origin: string;
  priceRange: string;
  description: string;
  image: string;
  featured: boolean;
}

function formatPriceRange(min: number | null, max: number | null): string {
  if (min && max) return `$${min.toLocaleString()} - $${max.toLocaleString()} USD`;
  if (min) return `From $${min.toLocaleString()} USD`;
  if (max) return `Up to $${max.toLocaleString()} USD`;
  return 'Request a Quote';
}

function toUI(product: ProductFromDB): ProductUI {
  return {
    id: product.slug,
    slug: product.slug,
    name: product.name,
    category: product.category,
    subcategory: product.subcategory || '',
    manufacturer: product.manufacturer,
    origin: product.origin,
    priceRange: formatPriceRange(product.price_min, product.price_max),
    description: product.description || '',
    image: (product.images && product.images.length > 0) ? product.images[0] : 'https://readdy.ai/api/search-image?query=Modern%20industrial%20product%20on%20clean%20white%20studio%20background%20professional%20product%20photography%20clean%20lighting%20isolated%20presentation&width=800&height=600&seq=product-placeholder&orientation=landscape',
    featured: product.featured,
  };
}

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedSubcategory, setSelectedSubcategory] = useState(searchParams.get('subcategory') || 'all');
  const [selectedOrigin, setSelectedOrigin] = useState('All Origins');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const [allProducts, setAllProducts] = useState<ProductUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchErr } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchErr) throw fetchErr;
      setAllProducts((data || []).map(toUI));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (selectedCategory !== 'all') params.category = selectedCategory;
    if (selectedSubcategory !== 'all') params.subcategory = selectedSubcategory;
    setSearchParams(params, { replace: true });
  }, [search, selectedCategory, selectedSubcategory, setSearchParams]);

  const activeCategory = productCategories.find((c) => c.id === selectedCategory);

  const filtered = allProducts.filter((p) => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.description.toLowerCase().includes(search.toLowerCase()) || p.manufacturer.toLowerCase().includes(search.toLowerCase());
    const matchCat = selectedCategory === 'all' || p.category === activeCategory?.name;
    const matchSub = selectedSubcategory === 'all' || p.subcategory === selectedSubcategory;
    const matchOrigin = selectedOrigin === 'All Origins' || p.origin === selectedOrigin;
    return matchSearch && matchCat && matchSub && matchOrigin;
  });

  const clearFilters = () => {
    setSearch('');
    setSelectedCategory('all');
    setSelectedSubcategory('all');
    setSelectedOrigin('All Origins');
  };

  const hasActiveFilters = search || selectedCategory !== 'all' || selectedSubcategory !== 'all' || selectedOrigin !== 'All Origins';

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-[80px]">
        {/* Header */}
        <div className="w-full px-6 md:px-10 lg:px-16 py-14 md:py-20 bg-background-100">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4"
          >
            Catalogue
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-6xl font-heading font-bold text-foreground-950 mb-4"
          >
            Product Catalogue
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-foreground-600 max-w-2xl text-sm md:text-base leading-relaxed"
          >
            Browse our extensive range of products from verified Chinese manufacturers. Use the filters and search to find exactly what you need — then request a quote.
          </motion.p>
        </div>

        <div className="w-full px-6 md:px-10 lg:px-16">
          {/* Search Bar */}
          <div className="py-6 md:py-8">
            <div className="relative max-w-xl">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products, manufacturers, or keywords..."
                className="w-full pl-11 pr-4 py-3 text-sm bg-background-50 border border-background-200 rounded-full focus:outline-none focus:border-accent-400 transition-colors text-foreground-800"
              />
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-24">
              <div className="w-10 h-10 border-2 border-accent-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-50 text-red-400">
                <i className="ri-error-warning-line text-2xl"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground-700 mb-2">Failed to load products</h3>
              <p className="text-sm text-foreground-500 mb-4">{error}</p>
              <button onClick={fetchProducts} className="px-5 py-2.5 text-sm font-medium bg-accent-500 text-primary-900 rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Try Again
              </button>
            </div>
          )}

          {/* Content */}
          {!loading && !error && (
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Sidebar Filters */}
              <div className="w-full lg:w-64 flex-shrink-0">
                <div className="lg:sticky lg:top-[100px] space-y-6">
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Category</h4>
                    <div className="space-y-1">
                      <button
                        onClick={() => { setSelectedCategory('all'); setSelectedSubcategory('all'); }}
                        className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-between ${
                          selectedCategory === 'all' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        All Categories
                        <span className="text-[10px] text-foreground-400">{allProducts.length}</span>
                      </button>
                      {productCategories.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => { setSelectedCategory(cat.id); setSelectedSubcategory('all'); }}
                          className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-between ${
                            selectedCategory === cat.id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-600 hover:bg-background-100'
                          }`}
                        >
                          {cat.name}
                          <span className="text-[10px] text-foreground-400">
                            {allProducts.filter((p) => p.category === cat.name).length}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {activeCategory && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Subcategory</h4>
                      <div className="space-y-1">
                        <button
                          onClick={() => setSelectedSubcategory('all')}
                          className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer ${
                            selectedSubcategory === 'all' ? 'bg-accent-50 text-accent-700 font-medium' : 'text-foreground-600 hover:bg-background-100'
                          }`}
                        >
                          All Subcategories
                        </button>
                        {activeCategory.subcategories.map((sub) => (
                          <button
                            key={sub}
                            onClick={() => setSelectedSubcategory(sub)}
                            className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer ${
                              selectedSubcategory === sub ? 'bg-accent-50 text-accent-700 font-medium' : 'text-foreground-600 hover:bg-background-100'
                            }`}
                          >
                            {sub}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Country of Origin</h4>
                    <div className="flex flex-wrap gap-2">
                      {origins.map((origin) => (
                        <button
                          key={origin}
                          onClick={() => setSelectedOrigin(origin)}
                          className={`px-3 py-1.5 text-xs rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                            selectedOrigin === origin ? 'bg-accent-500 text-primary-900 font-medium' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                          }`}
                        >
                          {origin}
                        </button>
                      ))}
                    </div>
                  </div>

                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-1.5 text-xs font-medium text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-close-circle-line"></i> Clear all filters
                    </button>
                  )}
                </div>
              </div>

              {/* Product Grid */}
              <div className="flex-1 min-w-0 pb-14 md:pb-20">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
                  <p className="text-sm text-foreground-500">
                    Showing <strong className="text-foreground-800">{filtered.length}</strong> of <strong className="text-foreground-800">{allProducts.length}</strong> products
                    {selectedCategory !== 'all' && activeCategory && (
                      <span className="text-foreground-400"> in {activeCategory.name}</span>
                    )}
                    {selectedSubcategory !== 'all' && (
                      <span className="text-foreground-400"> &rsaquo; {selectedSubcategory}</span>
                    )}
                  </p>
                  <div className="flex items-center gap-1 bg-background-100 rounded-full p-0.5">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer ${viewMode === 'grid' ? 'bg-background-50 text-foreground-800 shadow-sm' : 'text-foreground-400 hover:text-foreground-600'}`}
                      aria-label="Grid view"
                    >
                      <i className="ri-layout-grid-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer ${viewMode === 'list' ? 'bg-background-50 text-foreground-800 shadow-sm' : 'text-foreground-400 hover:text-foreground-600'}`}
                      aria-label="List view"
                    >
                      <i className="ri-list-check text-sm"></i>
                    </button>
                  </div>
                </div>

                {filtered.length === 0 && (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                      <i className="ri-search-line text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-foreground-700 mb-2">No products found</h3>
                    <p className="text-sm text-foreground-500 mb-4">Try adjusting your search or filter criteria</p>
                    <button onClick={clearFilters} className="px-5 py-2.5 text-sm font-medium bg-accent-500 text-primary-900 rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
                      Clear all filters
                    </button>
                  </div>
                )}

                {/* Grid View */}
                {viewMode === 'grid' && filtered.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {filtered.map((product, idx) => (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.4, delay: idx * 0.08 }}
                      >
                        <Link
                          to={`/products/${product.slug}`}
                          className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:border-accent-200/50 transition-all duration-400 cursor-pointer block"
                        >
                          <div className="relative aspect-[4/3] overflow-hidden bg-background-100">
                            <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                            <span className="absolute top-3 left-3 px-2.5 py-1 bg-background-50/90 backdrop-blur-sm text-[10px] font-semibold uppercase tracking-wider text-foreground-600 rounded-full">
                              {product.subcategory}
                            </span>
                            <span className="absolute top-3 right-3 px-2.5 py-1 bg-accent-500/90 backdrop-blur-sm text-[10px] font-semibold text-primary-900 rounded-full">
                              {product.origin}
                            </span>
                          </div>
                          <div className="p-5">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-[10px] text-foreground-400">{product.manufacturer}</span>
                            </div>
                            <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">{product.name}</h3>
                            <p className="text-xs text-foreground-500 mb-4 line-clamp-2">{product.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-heading font-bold text-primary-600">{product.priceRange}</span>
                              <span className="text-xs font-medium text-accent-600 whitespace-nowrap">Details <i className="ri-arrow-right-s-line"></i></span>
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* List View */}
                {viewMode === 'list' && filtered.length > 0 && (
                  <div className="space-y-4">
                    {filtered.map((product, idx) => (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.4, delay: idx * 0.08 }}
                      >
                        <Link
                          to={`/products/${product.slug}`}
                          className="group flex flex-col sm:flex-row bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:border-accent-200/50 transition-all duration-400 cursor-pointer block"
                        >
                        <div className="relative w-full sm:w-56 h-44 sm:h-auto flex-shrink-0 overflow-hidden bg-background-100">
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                        </div>
                        <div className="flex-1 p-5 flex flex-col justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span className="text-[10px] font-medium text-accent-600 bg-accent-50 px-2 py-0.5 rounded-full">{product.origin}</span>
                              <span className="text-[10px] text-foreground-400">{product.manufacturer}</span>
                              <span className="text-[10px] text-foreground-400">|</span>
                              <span className="text-[10px] text-foreground-400">{product.category} &rsaquo; {product.subcategory}</span>
                            </div>
                            <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors duration-200">{product.name}</h3>
                            <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">{product.description}</p>
                          </div>
                          <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-100">
                            <span className="text-base font-heading font-bold text-primary-600">{product.priceRange}</span>
                            <span className="inline-flex items-center gap-1 px-4 py-2 text-xs font-semibold bg-accent-500 text-primary-900 rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap">
                              Request Quote <i className="ri-arrow-right-line"></i>
                            </span>
                          </div>
                        </div>
                      </Link>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Categories Overview */}
          {!loading && !error && (
            <div className="w-full py-14 md:py-20">
              <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8">Browse by Category</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {productCategories.map((cat, idx) => (
                  <motion.button
                    key={cat.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: idx * 0.1 }}
                    onClick={() => { setSelectedCategory(cat.id); setSelectedSubcategory('all'); window.scrollTo({ top: 400, behavior: 'smooth' }); }}
                    className="group bg-background-50 border border-background-200/70 rounded-2xl p-6 hover:border-accent-200/50 transition-all duration-400 cursor-pointer text-left"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4 group-hover:bg-accent-500 group-hover:text-white transition-all duration-400">
                      <i className={`${cat.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed mb-3">{cat.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {cat.subcategories.slice(0, 4).map((sub) => (
                        <span key={sub} className="text-[10px] px-2 py-0.5 bg-background-100 text-foreground-500 rounded-full">
                          {sub}
                        </span>
                      ))}
                      {cat.subcategories.length > 4 && (
                        <span className="text-[10px] px-2 py-0.5 text-accent-600">+{cat.subcategories.length - 4} more</span>
                      )}
                    </div>
                    </motion.button>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}